import React from "react";
import { HStack, Text, VStack } from "@chakra-ui/react";
import { KGDataFrameProto } from "build-ts/dataframe";
import { ColumnType, TimeBucket } from "build-ts/enums";

export const COLUMN_TYPE_LABEL: Partial<Record<ColumnType, string>> = {
  [ColumnType.MEASURE]: "Measure",
  [ColumnType.ATTRIBUTE]: "Attribute",
  [ColumnType.TEMPORAL]: "Timeframe",
};

export const getTimeBucketLabel = (bucket: TimeBucket): string => {
  switch (bucket) {
    case TimeBucket.DAILY: return "daily";
    case TimeBucket.WEEKLY: return "weekly";
    case TimeBucket.MONTHLY: return "monthly";
    case TimeBucket.QUARTERLY: return "quarterly";
    case TimeBucket.YEARLY: return "yearly";
    default: return "";
  }
};

/**
 * Maps each output_column_name in a KGDataFrameProto to a tooltip ReactNode
 * describing its MKG entity (name, type, time bucket, benchmark).
 * Columns without a matching MKG entry are omitted from the result.
 */
export function buildKGColumnTooltips(data: KGDataFrameProto): Record<string, React.ReactNode> {
  const result: Record<string, React.ReactNode> = {};
  const kgObs = data.kg_observation;
  if (!kgObs) return result;

  const metrics = kgObs.metrics ?? [];
  const obsCols = kgObs.columns ?? [];
  const benchmarks = kgObs.benchmarks ?? [];

  for (const dfCol of data.ordered_columns ?? []) {
    const outName = dfCol.output_column_name;
    if (!outName) continue;

    const actualsId = dfCol.column_hash?.actuals_id;
    const benchmarkId = dfCol.column_hash?.benchmark_id;

    let entityName: string | null = null;
    let typeLabel: string | null = null;
    let timeBucketLabel: string | null = null;

    if (actualsId) {
      const metric = metrics.find((m) => m.header?.id === actualsId);
      if (metric?.header?.name) {
        entityName = metric.header.name;
        typeLabel = "Metric";
      } else {
        const obsCol = obsCols.find((c) => c.column_id === actualsId);
        if (obsCol?.kg_column_header?.name) {
          entityName = obsCol.kg_column_header.name;
          typeLabel = obsCol.column_type ? (COLUMN_TYPE_LABEL[obsCol.column_type] ?? null) : null;
          timeBucketLabel = obsCol.time_bucket ? getTimeBucketLabel(obsCol.time_bucket) : null;
        }
      }
    }

    if (!entityName) continue;

    let benchmarkLabel: string | null = null;
    if (benchmarkId) {
      const benchmark = benchmarks.find((b) => b.header?.id === benchmarkId);
      if (benchmark?.header?.name) benchmarkLabel = benchmark.header.name;
    }

    result[outName] = (
      <VStack align="flex-start" spacing={0.5} px={1} py={0.5}>
        <Text fontSize="xs" fontWeight="700" color="white" mb={0}>{entityName}</Text>
        <HStack spacing={1.5}>
          {typeLabel && (
            <Text fontSize="2xs" color="purple.200" mb={0} fontWeight="600">{typeLabel}</Text>
          )}
          {timeBucketLabel && (
            <Text fontSize="2xs" color="gray.300" mb={0}>· {timeBucketLabel}</Text>
          )}
        </HStack>
        {benchmarkLabel && (
          <Text fontSize="2xs" color="gray.300" mb={0}>vs {benchmarkLabel}</Text>
        )}
      </VStack>
    );
  }

  return result;
}
